import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Volume2, VolumeX, Play } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useVideoView } from "@/hooks/useVideoView";
import { LikeButton } from "@/components/music/LikeButton";
import { Comments } from "@/components/music/Comments";
import { UserAvatar } from "@/components/ui-nw/UserAvatar";
import { ShareButton } from "./ShareButton";
import { formatCount } from "@/lib/format";

const railBtn = "flex flex-col items-center gap-1 text-[11px] font-semibold text-white drop-shadow";

export const ShortItem = ({ video, active, muted, setMuted }) => {
  const el = useRef(null);
  const { src, ...handlers } = useVideoView(video, active);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [comments, setComments] = useState(false);
  const [count, setCount] = useState(video.comment_count);

  useEffect(() => {
    const v = el.current;
    if (!v || !src) return;
    if (active) { v.play().catch(() => setPaused(true)); } else { v.pause(); v.currentTime = 0; }
  }, [active, src]);

  const toggle = () => { const v = el.current; v.paused ? v.play() : v.pause(); };
  const seek = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    el.current.currentTime = ((e.clientX - r.left) / r.width) * (el.current.duration || 0);
  };

  return (
    <section data-testid={`short-item-${video.id}`} data-video-id={video.id} className="short-item relative h-full w-full snap-start snap-always bg-black">
      <video ref={el} src={src || undefined} poster={`${process.env.REACT_APP_BACKEND_URL}${video.thumbnail_url}`} muted={muted} loop playsInline preload="metadata"
        className="h-full w-full object-contain" onClick={toggle} data-testid={`short-video-${video.id}`}
        onPlay={() => { handlers.onPlay(); setPaused(false); }} onPause={() => setPaused(true)}
        onTimeUpdate={(e) => { handlers.onTimeUpdate(e); setProgress(e.currentTarget.currentTime / (e.currentTarget.duration || 1)); }}
        onSeeked={handlers.onSeeked} onError={handlers.onError} onLoadedMetadata={handlers.onLoadedMetadata} />
      {paused && active && <button onClick={toggle} aria-label="Play" data-testid={`short-play-${video.id}`} className="absolute inset-0 m-auto grid h-16 w-16 place-items-center rounded-full bg-black/50 text-white"><Play className="h-8 w-8 translate-x-[2px]" fill="currentColor" /></button>}
      <button data-testid={`short-mute-${video.id}`} onClick={() => setMuted(!muted)} aria-label={muted ? "Unmute" : "Mute"}
        className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur">
        {muted ? <><VolumeX className="h-4 w-4" /> Tap for sound</> : <Volume2 className="h-4 w-4" />}
      </button>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-black/90 to-transparent" />
      <div className="absolute right-3 bottom-28 md:bottom-24 flex flex-col items-center gap-5">
        {video.owner && <Link to={`/u/${video.owner.username}`} aria-label="Creator profile"><UserAvatar profile={video.owner} size="h-11 w-11" textSize="text-xs" /></Link>}
        <div className="rounded-full bg-black/40 p-2 backdrop-blur"><LikeButton track={video} kind="videos" /></div>
        <button data-testid={`short-comments-${video.id}`} onClick={() => setComments(true)} className={railBtn}><MessageCircle className="h-6 w-6" />{formatCount(count)}</button>
        <ShareButton path={`/watch/${video.id}`} title={video.title} label="Share" testId={`short-share-${video.id}`} className={railBtn} />
      </div>
      <div className="absolute left-4 right-20 bottom-10 md:bottom-6 space-y-1">
        {video.owner && <Link to={`/u/${video.owner.username}`} className="font-display font-bold text-white">@{video.owner.username}</Link>}
        <p className="font-semibold text-white">{video.title}</p>
        {video.description && <p className="line-clamp-2 text-xs text-slate-300">{video.description}</p>}
      </div>
      <div data-testid={`short-seek-${video.id}`} onClick={seek} className="absolute inset-x-0 bottom-0 h-4 cursor-pointer">
        <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20"><div className="h-full bg-nw-red" style={{ width: `${progress * 100}%` }} /></div>
      </div>
      <Sheet open={comments} onOpenChange={setComments}>
        <SheetContent side="bottom" className="bg-[#07080C] border-white/10 text-slate-100 rounded-t-3xl max-h-[75vh] overflow-y-auto">
          <SheetTitle className="sr-only">Comments</SheetTitle>
          <SheetDescription className="sr-only">Comments on {video.title}</SheetDescription>
          <Comments trackId={video.id} kind="videos" onCountChange={(d) => setCount((c) => c + d)} />
        </SheetContent>
      </Sheet>
    </section>
  );
};
